import { motion } from "framer-motion";
import { mediaUrl } from "../../lib/api";
import CaptionedImage from "../CaptionedImage";
import { useLang } from "../../lib/i18n";

export default function About({ gallery }) {
  const { t } = useLang();
  const contentImage = (key) => {
    const value = t(key);
    return value === key ? "" : value;
  };
  const img1 = mediaUrl(contentImage("about.image1") || gallery?.[0]?.image_url);
  const img2 = mediaUrl(contentImage("about.image2") || gallery?.[3]?.image_url || gallery?.[1]?.image_url);

  const stats = [1, 2].map((n) => ({
    value: t(`about.stat${n}_value`),
    label: t(`about.stat${n}_label`),
  }));

  return (
    <section id="about" data-testid="about-section" className="py-24 sm:py-32 bg-[#FBF9F5] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="text-xs uppercase tracking-[0.3em] font-sans font-semibold text-[#9A7B66] mb-5"
          >
            {t("about.eyebrow")}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif font-normal tracking-tight text-[#2C2825] leading-tight text-3xl sm:text-4xl lg:text-5xl"
          >
            {t("about.title")}
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-6 space-y-5"
          >
            {t("about.body").split(/\n\s*\n/).filter(Boolean).map((para, i) => (
              <p key={i} data-testid={`about-body-paragraph-${i}`} className="text-lg text-[#6E675F] font-sans font-light leading-relaxed">{para}</p>
            ))}
          </motion.div>
          <motion.blockquote
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-8 border-l-2 border-[#9A7B66] pl-5 font-serif italic text-xl text-[#9A7B66] leading-relaxed"
          >
            {t("about.quote")}
          </motion.blockquote>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-10 grid grid-cols-2 gap-6"
          >
            {stats.map((s) => (
              <div key={s.label}>
                <div className="font-serif text-4xl text-[#2C2825]">{s.value}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-[#A0988E] font-sans">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-7 relative h-[440px] sm:h-[560px]">
          <motion.div
            initial={{ clipPath: "inset(15% 15% 15% 15%)", opacity: 0 }}
            whileInView={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-0 top-0 w-[68%] h-[78%] rounded-md overflow-hidden shadow-2xl"
          >
            <CaptionedImage
              testId="about-photo-1"
              src={img1}
              alt={contentImage("about.image1_alt") || "Nusadira villa view"}
              title={contentImage("about.image1_title")}
              description={contentImage("about.image1_description")}
              caption={contentImage("about.image1_caption")}
              focalPoint={contentImage("about.image1_focal") || undefined}
              className="w-full h-full"
              imageClassName="w-full h-full object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ clipPath: "inset(15% 15% 15% 15%)", opacity: 0, y: 40 }}
            whileInView={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 bottom-0 w-[52%] h-[62%] rounded-md overflow-hidden shadow-2xl border-8 border-[#FBF9F5]"
          >
            <CaptionedImage
              testId="about-photo-2"
              src={img2}
              alt={contentImage("about.image2_alt") || "Nusadira villa detail"}
              title={contentImage("about.image2_title")}
              description={contentImage("about.image2_description")}
              caption={contentImage("about.image2_caption")}
              focalPoint={contentImage("about.image2_focal") || undefined}
              className="w-full h-full"
              imageClassName="w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
